import { useMemo } from 'react';

import { AppUtils } from '../../../store/apps/AppUtils';

import { useI18n } from '../../../hooks/useI18nFormatters';
import { useApp } from '../../../store/apps/useApps.ts';

import { ColumnItemDefault } from './explorer/ColumnItemDefault';
import { useFlowCardExplorerContext } from './FlowCardExplorerContext';

export function ColumnTypeApp(props) {
  const { i18n } = useI18n();

  const { sections } = useAppSections({
    i18n,
    entry: props.entry,
    columnProps: props.columnProps,
  });

  return (
    <props.listBoxComponent {...props.listBoxProps} aria-label="App column" items={sections} />
  );
}

export function AppItem(props) {
  const { app } = useApp({ appId: props.appId });
  const backgroundColor = AppUtils.getBackgroundColor(app);

  return <ColumnItemDefault iconObj={app?.iconObj} backgroundColor={backgroundColor} />;
}

function useAppSections({ i18n, entry, columnProps }) {
  const { cardType, cardFilter, onCardSelect } = columnProps;
  const cardTypePlural = `${cardType}s`;
  const flowCardExplorerContext = useFlowCardExplorerContext();
  const cards = flowCardExplorerContext[cardTypePlural];

  const app = useMemo(() => {
    return cards.byAppUri[entry.key] ?? {};
  }, [cards.byAppUri, entry.key]);

  const sections = useMemo(() => {
    const sections = [];

    const appDeviceItems = [];
    const appDeviceEntries = Object.entries(app.devices ?? {});

    for (const [deviceId, device] of appDeviceEntries) {
      appDeviceItems.push({
        key: deviceId,
        type: 'device',
        textValue: device.name,
        context: {},
      });
    }
    appDeviceItems.sort((a, b) => i18n.collator.compare(a.textValue, b.textValue));

    if (appDeviceItems.length > 0) {
      sections.push({
        key: 'devices',
        title: i18n.messageFormatter('flow.cardDialog.devices'),
        children: appDeviceItems,
      });
    }

    const cardItems = [];
    const cardsByKeyEntries = Object.entries(app.cardsByKey ?? {});

    for (const [cardKey, card] of cardsByKeyEntries) {
      if (cardFilter(card) === false) continue;

      const cardItem = {
        key: cardKey,
        type: 'card',
        textValue: card.title ?? card.titleFormatted,
        action() {
          onCardSelect(cardKey, card);
        },
        context: {
          card,
        },
      };

      // If the card is popular, add it to the start of the array.
      if (card.highlight === true) {
        cardItems.unshift(cardItem);
        continue;
      }

      cardItems.push(cardItem);
    }

    if (cardItems.length > 0) {
      sections.push({
        key: 'cards',
        title: i18n.messageFormatter('flow.cardDialog.cards'),
        children: cardItems,
      });
    }

    return sections;
  }, [i18n, cardFilter, onCardSelect, app]);

  return { sections };
}
